import { useLocalStorageState } from 'ahooks'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import * as com from '../Common.js'
import UpgradeToBusinessModal from '../Management/UpgradeToBusinessModal'
import './ChatInput.css'
import { CONTENT_TYPES, IS_ZEITRO, PROMPT_EXAMPLES, IS_WHITE_LIST, IS_IFRAMES, getIframesToken } from './constants'
import { useAnonymousId } from './useAnonymousId'
import InputHint from './components/InputHint'
import useScenarioUsed from './hooks/useScenarioUsed'
import useTextareaPlaceholder from './hooks/useTextareaPlaceholder'
import { useTrackEvent } from './analytics/useTrackEvent'
import { Tooltip } from 'antd'
import Mode from './components/Mode'
import RemainingCount from './components/RemainingCount'
import { PATH_CUSTOMER_ID } from './constants'
import Voice from './components/Voice/'
import VoiceWaveform from './components/Voice/VoiceWaveform'

const SendIcon = ({ active }) => (
    <img
        src={
            active
                ? '/images/send-icon-active.svg'
                : '/images/send-icon-inactive.svg'
        }
        alt={active ? 'Send icon active' : 'Send icon inactive'}
        // width="26"
        // height="26"
        className={`send-icon ${active ? 'active' : 'inactive'}`}
    />
)

function ChatInput({
    anomaly,
    isPaid,
    onSendMessage,
    onScenarioSearch,
    onShowPayWall,
    style,
    isBotThinking,
    isReceivingAnswer,
    docName,
    fromLanding,
    usage,
    isLandingWelcome,
    threadID,
    isMobile,
    journeySubmitted,
    programs,
    isFreddieMacStandalone = false,
    isFannieMaeStandalone = false,
    setShowIframeLoginModal,
    activeIframeSession,
    profile,
    companyPayMode,
    setSignType,
    signupGuide,
    setShowSelfPayUpgradeModal,
    setTriggerType,
    currentBillingStatus,
    promoCodeValidity,
    setApplyPromoCode,
}) {
    const { trackQuestionAsk, trackStandaloneActive, trackDailyLimitReached, trackLimitDemoClick } = useTrackEvent()
    const anonymousId = useAnonymousId()
    const hasTrackedLimitReached = useRef(false)
    const [inputValue, setInputValue] = useState('')
    const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false)
    const [isTextareaFocused, setIsTextareaFocused] = useState(false)
    const textareaRef = useRef(null)

    // 语音录音状态和音频数据
    const [isRecording, setIsRecording] = useState(false)
    const [isVoiceUploading, setIsVoiceUploading] = useState(false)
    const [audioData, setAudioData] = useState(null)
    // Voice 组件引用，用于停止录音
    const voiceRef = useRef(null)

    // 路由变化监听：当路由变化时自动停止录音
    const location = useLocation()
    const prevPathnameRef = useRef(location.pathname)
    useEffect(() => {
        // 检查路径是否真的发生了变化（排除首次渲染）
        if (prevPathnameRef.current !== location.pathname) {
            prevPathnameRef.current = location.pathname
            // 如果正在录音或上传中，停止录音并重置状态
            if (isRecording || isVoiceUploading) {
                if (voiceRef.current?.stopRecording) {
                    voiceRef.current.stopRecording()
                }
                setIsRecording(false)
                setIsVoiceUploading(false)
                setAudioData(null)
            }
        }
    }, [location.pathname, isRecording, isVoiceUploading])

    // const [model, setModel] = useState(
    //     sessionStorage.getItem("scenario_model") || "instant",  // instant deepthinking
    // );

    const source = useSelector((state) => state.common.source)

    const pdfType = useSelector((state) => state.common.content)
    const isScenario = pdfType === CONTENT_TYPES.SCENARIO_GMCC || pdfType === CONTENT_TYPES.SCENARIO_DESK

    const prompts = useMemo(() => PROMPT_EXAMPLES[pdfType] || [], [pdfType])

    // 从 prompts 中提取问题文本用于 placeholder 轮播
    const promptQuestions = useMemo(() =>
        prompts.map(prompt => prompt.question).filter(Boolean),
        [prompts]
    )

    // 使用自定义的问题列表进行 placeholder 轮播
    const dynamicPlaceholder = useTextareaPlaceholder(promptQuestions)

    const subscriptionReducer = useSelector(
        (state) => state.subscriptionReducer?.currentInfo
    )

    const [questionCount] = useLocalStorageState('gptQuestionCount', {
        defaultValue: 0,
        listenStorageChange: true,
    })
    const isLoggedIn = !!sessionStorage.getItem('ZeitroA') || !!localStorage.getItem(getIframesToken());
    let effectiveQuestionCount = 0
    // const effectiveQuestionCount = useMemo(() => {
    if (isLoggedIn) {
        effectiveQuestionCount = usage || 0;
    } else {
        effectiveQuestionCount = questionCount || 0;
    }
    // }, [isLoggedIn, fromLanding, usage, questionCount])


    const remaining = useMemo(() => 3 - effectiveQuestionCount, [effectiveQuestionCount])

    // Whether to show remaining tag based on journey submission status
    const showRemainingTag = useMemo(() => {
        if (!fromLanding || isFreddieMacStandalone || isFannieMaeStandalone) return false
        // If remaining < 4, user must have submitted the form to continue chatting
        if (remaining < 4) return true
        // If remaining === 4, use the journey status passed from parent
        if (remaining === 4) return journeySubmitted
        // If remaining > 4, user hasn't reached the continue journey step yet
        return false
    }, [fromLanding, remaining, journeySubmitted, isFreddieMacStandalone, isFannieMaeStandalone])

    // function onModelChange(value) {
    //     sessionStorage.setItem('scenario_model', value);
    //     setModel(value)
    // }

    const getFreeStatus = useCallback(() => {
        return com.getFreeStatus(
            subscriptionReducer.plan,
            subscriptionReducer.loanOfficers
        )
    }, [subscriptionReducer.plan, subscriptionReducer.loanOfficers])

    const isOneFreeUsage = useMemo(() => {
        if (isScenario) return false
        return getFreeStatus() && usage === 4
    }, [getFreeStatus, isScenario, usage])

    const isFreeLimitReached = useMemo(() => {
        if (isScenario) return false
        return getFreeStatus() && usage >= 5
    }, [getFreeStatus, isScenario, usage])

    const isButtonActive = useMemo(() => {
        // 在非 Scenario 模式下，有输入内容或（有 placeholder 且输入框失去焦点）都可以发送
        // 在 Scenario 模式下，只有有输入内容才能发送
        const hasContent = inputValue.trim() || (!isScenario && dynamicPlaceholder && !isTextareaFocused)
        return (
            hasContent &&
            !isBotThinking &&
            !isReceivingAnswer && // isReceivingAnswer now only becomes false when answer is fully complete
            (isFreddieMacStandalone || isFannieMaeStandalone || !isFreeLimitReached)
        )
    }, [inputValue, isScenario, dynamicPlaceholder, isTextareaFocused, isReceivingAnswer, isFreeLimitReached, isBotThinking, isFreddieMacStandalone, isFannieMaeStandalone])

    // 检查当前用户的计划类型
    const currentPlan = useMemo(() => {
        return subscriptionReducer?.plan || 'free'
    }, [subscriptionReducer?.plan])

    // 处理显示付费墙的逻辑
    const handleShowPayWall = useCallback(() => {
        // 在Freddie Mac standalone模式下不显示付费墙
        if (isFreddieMacStandalone || isFannieMaeStandalone) return

        // 如果当前 plan 是 starter，显示升级弹窗
        if (currentPlan === 'starter') {
            setIsUpgradeModalOpen(true)
            return
        }

        // 否则调用原有的 onShowPayWall
        if (onShowPayWall) {
            onShowPayWall()
        }
    }, [currentPlan, onShowPayWall, isFreddieMacStandalone, isFannieMaeStandalone])

    const handleCloseUpgradeModal = () => {
        setIsUpgradeModalOpen(false)
    }

    const handleUpgrade = () => {
        setIsUpgradeModalOpen(false)
        // 跳转到定价页面
        sessionStorage.setItem('path', 'Pricing')
        window.open('/app/management', '_self')
    }



    useEffect(() => {
        adjustTextareaHeight()
    }, [inputValue])




    const handleTextareaClick = useCallback(() => {
        if (isFreeLimitReached) {
            handleShowPayWall()
        }
    }, [isFreeLimitReached, handleShowPayWall])

    // Handle textarea focus
    const handleTextareaFocus = useCallback(() => {
        setIsTextareaFocused(true)
    }, [])

    // Handle textarea blur
    const handleTextareaBlur = useCallback(() => {
        setIsTextareaFocused(false)
    }, [])



    const adjustTextareaHeight = useCallback(() => {
        if (isMobile) return

        const textarea = textareaRef.current
        if (textarea) {
            textarea.style.height = 'auto'
            const scrollHeight = textarea.scrollHeight
            const maxHeight = 6 * 24

            // When the content exceeds 5 lines, add the scrollable class
            if (scrollHeight <= maxHeight) {
                textarea.style.height = `${scrollHeight}px`
                textarea.classList.remove('scrollable')
            } else {
                textarea.style.height = `${maxHeight}px`
                textarea.classList.add('scrollable')
            }
        }
    }, [isMobile])

    const handleSend = useCallback(() => {
        if (anomaly?.MODEL_ERROR || anomaly?.DATABASE_ERROR) return; // model、database错误不能发起
        if (IS_IFRAMES && SCENARIO_AI_USE.DISABLED && profile?.AccountTier !== 'paid') {
            return;
        }
        if (fromLanding) {
            trackQuestionAsk()
            // Track standalone active event for retention analysis (D1/D3/D5/D7)
            trackStandaloneActive()
        }

        // Create a function to clear the input
        const clearInput = () => {
            setInputValue('')
            textareaRef.current.style.height = 'auto'
        }

        // 使用输入值或当前 placeholder 作为问题
        const questionToSend = inputValue.trim() || dynamicPlaceholder

        if (isScenario) {
            if (onScenarioSearch) {
                onScenarioSearch(questionToSend)
                clearInput()
            }
        } else {
            if (onSendMessage) {
                onSendMessage(questionToSend, docName, clearInput)
            }
        }
    }, [inputValue, dynamicPlaceholder, isScenario, onSendMessage, onScenarioSearch, docName, anomaly, fromLanding, anonymousId, trackQuestionAsk, trackStandaloneActive])

    const verifyAndSendQuery = useCallback(() => {
        if (localStorage.getItem(getIframesToken())) {
            handleSend();
        } else {
            setShowIframeLoginModal(true);
        }
    }, [setShowIframeLoginModal, handleSend]);


    // iframe模式下，需要先验证iframe session是否存在，不存在则弹出iframe登录modal
    const handleClickSend = useCallback(() => {
        if (IS_IFRAMES) {
            verifyAndSendQuery()
        } else {
            if(signupGuide) return setSignType('SIGNIN')
            handleSend()
        }
    }, [IS_IFRAMES, handleSend, verifyAndSendQuery, signupGuide])

    const handleKeyPress = useCallback(
        (e) => {
            if (e.key !== 'Enter') return

            e.preventDefault()

            if (!isButtonActive) return

            if (!e.shiftKey) {
                // handleClickSend 会判断是否是iframe模式，iframe模式下没有iframe session则不能直接发送query
                handleClickSend()
            }
        },
        [isButtonActive, handleClickSend]
    )

    const handleInputChange = useCallback(
        (e) => {
            setInputValue(e.target.value)
            adjustTextareaHeight()
        },
        [adjustTextareaHeight]
    )

    // 处理音频数据回调（用于声波可视化）
    const handleAudioData = useCallback((data) => {
        // 创建新的 Uint8Array 副本，避免共享引用问题
        setAudioData(new Uint8Array(data))
    }, [])

    // 处理录音状态变化回调
    const handleRecordingChange = useCallback((recording) => {
        setIsRecording(recording)
        if (!recording) {
            // 录音结束时清空音频数据
            setAudioData(null)
        }
    }, [])

    // 处理 Voice 组件状态变化（用于显示上传 loading）
    const handleVoiceStatusChange = useCallback((status) => {
        // status: 'idle' | 'recording' | 'uploading'
        setIsVoiceUploading(status === 'uploading')
    }, [])

    // 停止录音
    const handleStopRecording = useCallback(() => {
        if (voiceRef.current?.stopRecording) {
            voiceRef.current.stopRecording()
        }
    }, [])

    // 处理语音转文字结果，将文本追加到输入框（增量更新）
    const handleVoiceTranscribed = useCallback((text) => {
        if (text && typeof text === 'string') {
            setInputValue((prev) => {
                // 如果已有内容，在末尾追加空格和新文本；否则直接使用新文本
                if (prev && prev.trim()) {
                    return prev.trim() + ' ' + text
                }
                return text
            })
        }
    }, [])

    const SCENARIO_AI_USE = useScenarioUsed();

    let ACTIVE_GPT_MODE = sessionStorage.getItem('activeGPTmode');

    // 是否需要停止用户继续咨询、并警告
    const STOP_ASKING = (function () {
        // NonQM 类似场景，没有<WelcomeGuideline/>页面，就就不会初始化activeGPTmode，导致disabled
        // 所以这里给个默认值
        if (!['guideline', 'scenario'].includes(ACTIVE_GPT_MODE)) {
            ACTIVE_GPT_MODE = 'scenario';
            sessionStorage.setItem('activeGPTmode', 'scenario');
        }
        // 白名单不限次数
        if (IS_WHITE_LIST) return false;
        switch (ACTIVE_GPT_MODE) {
            case 'guideline':
                // In iframe mode, guideline and scenario queries are counted together
                if (IS_IFRAMES) {
                    return SCENARIO_AI_USE.DISABLED && profile?.AccountTier !== 'paid' && activeIframeSession;
                }
                // Agency guidelines 走老的逻辑
                return isLandingWelcome;
            case 'scenario':
                // Scenario AI 付费不限次数，未付费不得超过3次
                return (!IS_IFRAMES && !SCENARIO_AI_USE.PAID && SCENARIO_AI_USE.DISABLED) || (IS_IFRAMES && SCENARIO_AI_USE.PAID && SCENARIO_AI_USE.DISABLED && profile?.AccountTier !== 'paid' && activeIframeSession);
            default: return true;
        }
    })();

    // 剩余可用次数
    const COUNT = (function () {
        switch (ACTIVE_GPT_MODE) {
            case 'guideline':
                // In iframe mode, guideline and scenario queries are counted together
                if (IS_IFRAMES) {
                    return SCENARIO_AI_USE.REMAINING;
                }
                // Agency guidelines 走老的逻辑
                return remaining;
            case 'scenario':
                // Scenario AI 付费不限次数，未付费不得超过3次
                return SCENARIO_AI_USE.REMAINING;
            default: return true;
        }
    })();

    // 未选择任何 “guidelines” 时，实际上会全选所有的 "programs"
    // const GUIDELINE_COUNT = useMemo(() => (
    //     source?.length ? source?.length : programs.data.GUIDELINES.length
    // ), [source, programs]);

    const onUpgradeClick = () => {
        setTriggerType('usage-limit-reached');
        if (PATH_CUSTOMER_ID === 'successlending' && currentBillingStatus === 'inactive' && promoCodeValidity === 'valid') {
            setApplyPromoCode(true);
        }
        setShowSelfPayUpgradeModal(true);
    }

    function onSkip() {
        window.open("https://www.zeitro.com/demo", "_blank");
    }

    // Track daily limit reached event for standalone page (fromLanding = true)
    useEffect(() => {
        if (fromLanding && STOP_ASKING && isScenario && !hasTrackedLimitReached.current) {
            trackDailyLimitReached();
            hasTrackedLimitReached.current = true;
        }
    }, [fromLanding, STOP_ASKING, isScenario, trackDailyLimitReached]);

    // Handle "Go Unlimited" click with tracking for standalone page
    const handleLimitDemoClick = useCallback(() => {
        if (fromLanding) {
            trackLimitDemoClick();
        }
        window.open("https://www.zeitro.com/demo", "_blank");
    }, [fromLanding, trackLimitDemoClick]);

    const SERVER_ERROR = anomaly?.MODEL_ERROR || anomaly?.DATABASE_ERROR; // 数据库、模型错误，都会导致无法继续查询

    return (
        <div
            className="chat-input"
            style={{
                ...style,
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: '100%',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        maxWidth: isLandingWelcome ? '620px' : '',
                        position: 'relative',
                    }}
                >
                    {isOneFreeUsage && !isFreddieMacStandalone && !isFannieMaeStandalone && (
                        <div className="chat-limit-free-tag">Free chats: 1/5</div>
                    )}
                    {isFreeLimitReached && !isFreddieMacStandalone && !isFannieMaeStandalone && !IS_IFRAMES && (
                        <div className="chat-limit-info">
                            <div style={{ display: 'flex', gap: 8, flex: 1 }}>
                                {/* <img
                                    style={{ height: 12, marginTop: 4 }}
                                    src="/images/chat-limit-icon.svg"
                                /> */}
                                <div className="chat-limit-text">
                                    You've reached limit for free chat sessions.
                                    Ready for more?
                                </div>
                            </div>
                            <div
                                className="chat-limit-btn"
                                onClick={handleShowPayWall}
                            >
                                Go unlimited
                                <img src="/images/blue-arrow.svg" />
                            </div>
                        </div>
                    )}
                    <div
                        style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'flex-end',
                            position: 'relative',
                        }}
                        onClick={handleTextareaClick}
                    >
                        {/* Free chats remaining indicator - positioned relative to textarea */}
                        {/* {showRemainingTag && (
                            <div
                                className={`free-chats-remaining free-chats-textarea-top ${
                                    remaining <= 1 ? 'yellow' : 'green'
                                }`}
                            >
                                Free chats today: {remaining}
                            </div>
                        )} */}
                        <InputHint top={-38}
                            type='error'
                            onClick={onSkip}
                            buttonText='Contact us'
                            show={anomaly?.MODEL_ERROR}
                            text='System is experiencing temporary disruption - will be back shortly!'
                        />
                        <InputHint
                            top={-38}
                            onClick={handleLimitDemoClick}
                            buttonText='Go unlimited'
                            show={STOP_ASKING && isScenario}
                        />
                        <InputHint
                            top={-38}
                            buttonText='Upgrade'
                            onClick={onUpgradeClick}
                            show={IS_IFRAMES && profile?.AccountTier !== 'paid' && activeIframeSession && STOP_ASKING}
                        />
                        {(isPaid || (IS_IFRAMES && activeIframeSession)) && <Voice
                            ref={voiceRef}
                            onAudioData={handleAudioData}
                            onRecordingChange={handleRecordingChange}
                            onTranscribed={handleVoiceTranscribed}
                            onStatusChange={handleVoiceStatusChange}
                            disabled={STOP_ASKING}
                        />}
                        <div className="chat-textarea-layout">
                            {(isRecording || isVoiceUploading) ? (
                                // 录音中或上传中显示声波可视化
                                <VoiceWaveform
                                    audioData={audioData}
                                    onStop={handleStopRecording}
                                    barCount={isMobile ? 35 : 60}
                                    isLoading={isVoiceUploading}
                                />
                            ) : (
                                <textarea
                                    ref={textareaRef}
                                    className="chat-textarea"
                                    disabled={STOP_ASKING || SERVER_ERROR}
                                    value={inputValue}
                                    onChange={handleInputChange}
                                    onFocus={handleTextareaFocus}
                                    onBlur={handleTextareaBlur}
                                    onKeyPress={handleKeyPress}
                                    placeholder={isScenario ? "Ask me anything about guidelines..." : dynamicPlaceholder}
                                    rows={2}
                                />
                            )}

                            {!isRecording && (ACTIVE_GPT_MODE === 'guideline' || isFreddieMacStandalone || isFannieMaeStandalone || IS_IFRAMES) ? null : !isRecording && <div className='scenario-chat-tools' style={{ paddingRight: 12 }}>
                                {isScenario ? <Mode paddingLeft={2} /> : <span />}
                                {!SCENARIO_AI_USE.PAID && <RemainingCount count={COUNT} />}
                            </div>}
                            {!isRecording && IS_IFRAMES && <div className='scenario-chat-tools' style={{ paddingRight: 12 }}>
                                {isScenario ? <Mode paddingLeft={2} /> : <span />}
                                {profile?.AccountTier !== 'paid' && activeIframeSession && <RemainingCount count={COUNT} />}
                            </div>}
                        </div>
                        {!isRecording && !isVoiceUploading && (
                            <div
                                className="send-button"
                                onClick={handleClickSend}
                                style={{
                                    pointerEvents: isButtonActive ? 'auto' : 'none',
                                }}
                            >
                                <SendIcon active={isButtonActive && inputValue.trim().length > 0} />
                            </div>
                        )}
                    </div>
                </div>
                <div
                    className="gpt-limit-reached-text"
                >
                    AI can make mistakes, validate responses
                </div>
            </div>
            <UpgradeToBusinessModal
                isOpen={isUpgradeModalOpen}
                onClose={handleCloseUpgradeModal}
                onUpgrade={handleUpgrade}
                onViewPlanDetail={() => { }}
                upgradeTo="individual"
            />
        </div>
    )
}

export default ChatInput
